import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Link, useLocation } from "react-router-dom";
import "./App.css";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";

const APP_STORE_URL = "https://apps.apple.com/ph/app/kairos-focus-timer/id6761332345";

// ── Inline App Store Badge SVG ────────────────────────────────

function AppStoreBadge() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="160"
      height="54"
      viewBox="0 0 160 54"
      aria-label="Download on the App Store"
      role="img"
    >
      {/* Background */}
      <rect width="160" height="54" rx="10" ry="10" fill="#000" />
      <rect x="0.5" y="0.5" width="159" height="53" rx="9.5" ry="9.5" fill="none" stroke="#a6a6a6" strokeWidth="1" />

      {/* Apple logo */}
      <path
        d="M37.3 27.2c0-4.3 3.5-6.4 3.7-6.5-2-2.9-5.1-3.3-6.2-3.4-2.6-.3-5.2 1.6-6.5 1.6-1.4 0-3.4-1.6-5.6-1.5-2.8.1-5.5 1.7-6.9 4.2-3 5.2-.8 12.8 2.1 17 1.4 2.1 3.1 4.4 5.3 4.3 2.1-.1 2.9-1.4 5.5-1.4 2.5 0 3.3 1.4 5.5 1.3 2.3 0 3.8-2.1 5.2-4.2 1.6-2.4 2.3-4.7 2.3-4.8-.1-.1-4.4-1.7-4.4-6.6z"
        fill="#fff"
      />
      <path
        d="M33.1 14.9c1.2-1.4 2-3.4 1.7-5.3-1.7.1-3.7 1.1-4.9 2.5-1.1 1.2-2 3.2-1.7 5.1 1.8.1 3.7-.9 4.9-2.3z"
        fill="#fff"
      />

      {/* "Download on the" text */}
      <text x="55" y="20" fontFamily="-apple-system, 'Helvetica Neue', sans-serif" fontSize="10" fill="#fff" letterSpacing="0.3">
        Download on the
      </text>

      {/* "App Store" text */}
      <text x="53" y="38" fontFamily="-apple-system, 'Helvetica Neue', sans-serif" fontSize="20" fontWeight="600" fill="#fff" letterSpacing="-0.5">
        App Store
      </text>
    </svg>
  );
}

// ── Nav ───────────────────────────────────────────────────────

function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    if (location.pathname !== "/") {
      setScrolled(true);
      return;
    }
    const onScroll = () => setScrolled(window.scrollY > 1);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  return (
    <nav className={`nav${scrolled ? " nav-bordered" : ""}`}>
      <div className="nav-inner">
        <Link to="/" className="logo">
          <img src="/images/app-icon.png" alt="Kairos logo" className="logo-icon" />
          <span className="logo-name">kairos</span>
        </Link>
      </div>
    </nav>
  );
}

// ── Hero ─────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="hero-section">
      <div className="hero">
        {/* Float speed: --float-duration | Float height: --float-distance (in App.css :root) */}
        <div className="hero-visual fade-in">
          <img src="/images/app-icon-transparent.png" alt="Kairos app" className="hero-img hero-img-float" />
        </div>
        <div className="hero-text fade-in delay-1">
          <p className="hero-eyebrow">Available on the App Store</p>
          <h1>The focused, fun, and effective way to own your time.</h1>

          <a
            href={APP_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="appstore-btn"
          >
            <AppStoreBadge />
          </a>
        </div>
      </div>
    </section>
  );
}

// ── Alternating Sections ──────────────────────────────────────

const sections = [
  {
    id: "focus",
    eyebrow: "Deep focus",
    title: "Own your attention.",
    desc: "Attention is your most valuable currency. Set a timer and dive into deep work without distractions.",
    img: "/images/features_1.gif",
    imgAlt: "Focus session illustration",
    reverse: false,
  },
  {
    id: "streaks",
    eyebrow: "Streak system",
    title: "Build habits that actually stick.",
    desc: "Let focus be your lifestyle. Hit your daily session goal and keep your streak alive.",
    img: "/images/features_2.gif",
    imgAlt: "Streak system illustration",
    reverse: true,
  },
  {
    id: "history",
    eyebrow: "Session Analytics",
    title: "See exactly how you spend your time.",
    desc: "No more guessing where the day went. Spot your best days, busiest weeks, and long-term patterns.",
    img: "/images/features_3.gif",
    imgAlt: "Session history illustration",
    reverse: false,
  },
];

function AlternatingSections() {
  return (
    <div className="alt-sections">
      {sections.map((s, i) => (
        <section
          key={s.id}
          id={s.id}
          className={`alt-section${s.reverse ? " reverse" : ""}`}
        >
          <div className="alt-inner">
            <div className="alt-img-wrap fade-in" style={{ transitionDelay: `${i * 0.05}s` }}>
              <img src={s.img} alt={s.imgAlt} className="alt-img" />
            </div>
            <div className="alt-text fade-in" style={{ transitionDelay: `${i * 0.05 + 0.1}s` }}>
              <p className="section-eyebrow">{s.eyebrow}</p>
              <h2 className="alt-title">{s.title}</h2>
              <p className="alt-desc">{s.desc}</p>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}

// ── CTA ───────────────────────────────────────────────────────

function CTA() {
  return (
    <div className="cta-wrap">
      <div className="cta-inner fade-in">
        <h2>Your best work starts now.</h2>
        <p>Join thousands of people who use Kairos to do more of what matters.</p>
        <a
          href={APP_STORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-cta"
        >
          Download on the App Store
        </a>
      </div>
    </div>
  );
}

// ── Footer ────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="footer">
      <div className="footer-inner">
        <Link to="/" className="footer-brand">kairos</Link>
        <ul className="footer-links">
          <li><Link to="/about">About</Link></li>
          <li><Link to="/faq">FAQ</Link></li>
          <li><Link to="/contact">Contact</Link></li>
          <li><Link to="/privacy">Privacy</Link></li>
          <li><Link to="/terms">Terms</Link></li>
        </ul>
        <span className="footer-copy">© 2026 kairos</span>
      </div>
    </footer>
  );
}

// ── Fade-in hook ──────────────────────────────────────────────

function useFadeIn() {
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add("visible");
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-in").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);
}

// ── Scroll to top on route change ────────────────────────────

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// ── Home page ─────────────────────────────────────────────────

function HomePage() {
  useFadeIn();
  return (
    <>
      <Hero />
      <div className="hero-features-divider" />
      <AlternatingSections />
      <CTA />
    </>
  );
}

// ── Legal page shell ──────────────────────────────────────────

function LegalPage({ title, lastUpdated, children }) {
  return (
    <div className="legal-page">
      <div className="legal-inner">
        <p className="legal-eyebrow">kairos</p>
        <h1 className="legal-title">{title}</h1>
        <p className="legal-date">Last updated: {lastUpdated}</p>
        <div className="legal-divider" />
        <div className="legal-body">{children}</div>
      </div>
    </div>
  );
}

function LegalSection({ title, children }) {
  return (
    <div className="legal-section">
      <h2>{title}</h2>
      {children}
    </div>
  );
}

// ── Privacy page ──────────────────────────────────────────────

function PrivacyPage() {
  return (
    <LegalPage title="Privacy Policy" lastUpdated="March 11, 2026">
      <LegalSection title="Overview">
        <p>Kairos ("we", "us", or "our") is committed to protecting your privacy. This policy explains what information we collect, how we use it, and your rights regarding your data.</p>
      </LegalSection>
      <LegalSection title="Information We Collect">
        <p>We collect information you provide directly to us when you create an account or use the app, including:</p>
        <ul>
          <li>Account information such as your name and email address</li>
          <li>Usage data including session history, and streaks</li>
          <li>Device information such as operating system and app version</li>
          <li>Communications you send us, such as support requests or feedback</li>
        </ul>
      </LegalSection>
      <LegalSection title="How We Use Your Information">
        <p>We use the information we collect to:</p>
        <ul>
          <li>Provide, maintain, and improve the Kairos app and website</li>
          <li>Personalise your experience and track your focus progress</li>
          <li>Send you important updates, notifications, and support responses</li>
          <li>Analyse usage patterns to improve our features</li>
          <li>Comply with legal obligations</li>
        </ul>
      </LegalSection>
      <LegalSection title="Data Sharing">
        <p>We do not sell your personal information. We may share your data with trusted third-party service providers who help us operate the app, but only to the extent necessary and under strict confidentiality agreements.</p>
      </LegalSection>
      <LegalSection title="Data Retention">
        <p>We retain your data for as long as your account is active or as needed to provide services. You may request deletion of your account and associated data at any time by contacting us.</p>
      </LegalSection>
      <LegalSection title="Your Rights">
        <p>Depending on your location, you may have the right to access, correct, or delete your personal data. To exercise any of these rights, please contact us.</p>
      </LegalSection>
      <LegalSection title="Cookies">
        <p>Our website may use cookies to improve your browsing experience. You can control cookie settings through your browser preferences.</p>
      </LegalSection>
    </LegalPage>
  );
}

// ── Terms page ────────────────────────────────────────────────

function TermsPage() {
  return (
    <LegalPage title="Terms of Service" lastUpdated="March 11, 2026">
      <LegalSection title="Acceptance of Terms">
        <p>By downloading, accessing, or using Kairos, you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the app or website.</p>
      </LegalSection>
      <LegalSection title="Use of the App">
        <p>Kairos grants you a limited, non-exclusive, non-transferable licence to use the app for your personal, non-commercial purposes. You agree not to:</p>
        <ul>
          <li>Copy, modify, or distribute any part of the app without our written permission</li>
          <li>Attempt to reverse engineer or extract the source code</li>
          <li>Use the app for any unlawful or harmful purpose</li>
          <li>Interfere with the security or integrity of the service</li>
        </ul>
      </LegalSection>
      <LegalSection title="Accounts">
        <p>You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your account. Please notify us immediately if you suspect any unauthorised use of your account.</p>
      </LegalSection>
      <LegalSection title="Subscriptions & Payments">
        <p>Some features of Kairos require a paid subscription. All purchases are processed securely through third-party payment providers. Subscription fees are billed in advance on a recurring basis. You may cancel at any time, effective at the end of the current billing period.</p>
      </LegalSection>
      <LegalSection title="Intellectual Property">
        <p>All content, features, and functionality in Kairos — including text, graphics, logos, and software — are the property of Kairos and are protected by applicable intellectual property laws.</p>
      </LegalSection>
      <LegalSection title="Disclaimer of Warranties">
        <p>Kairos is provided on an "as is" and "as available" basis without warranties of any kind. We do not warrant that the app will be uninterrupted, error-free, or completely secure.</p>
      </LegalSection>
      <LegalSection title="Limitation of Liability">
        <p>To the fullest extent permitted by law, Kairos shall not be liable for any indirect, incidental, special, or consequential damages arising out of your use of the app.</p>
      </LegalSection>
      <LegalSection title="Changes to Terms">
        <p>We may update these Terms from time to time. Continued use of Kairos after changes are posted constitutes your acceptance of the revised Terms.</p>
      </LegalSection>
    </LegalPage>
  );
}

// ── About page ────────────────────────────────────────────────

function AboutPage() {
  return (
    <div className="legal-page">
      <div className="legal-inner">
        <p className="legal-eyebrow">Our story</p>
        <h1 className="legal-title">Built for people who want to do their best work.</h1>
        <div className="legal-divider" />
        <div className="legal-body">
          <div className="legal-section">
            <h2>Why we built Kairos</h2>
            <p>Kairos started with a simple frustration: it's hard to stay focused. Notifications, distractions, and endless to-do lists make deep work feel impossible. We believed there had to be a better way.</p>
            <p>Inspired by the science of time management and the habit-forming power of apps, we built Kairos — a timer app that doesn't just count down, but helps you build real, lasting focus habits.</p>
          </div>
          <div className="legal-section">
            <h2>What "Kairos" means</h2>
            <p>In ancient Greek, <em>Kairos</em> (καιρός) means the right, critical, or opportune moment — as opposed to chronological time. It's the idea that not all time is equal; what matters is how you use it.</p>
            <p>That's exactly what we want to help you do.</p>
          </div>
          <div className="legal-section">
            <h2>Our approach</h2>
            <p>We take the Pomodoro Technique — one of the most research-backed productivity methods — and layer on the motivation mechanics that make habit apps so effective: streaks, progress tracking, and gentle reminders that keep you accountable without being annoying.</p>
            <p>The result is a focus tool that feels less like a stopwatch and more like a coach.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Contact page ──────────────────────────────────────────────

function ContactPage() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleChange = e => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    const res = await fetch("https://formspree.io/f/myknwqla", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(form),
    });
    if (res.ok) {
      setSubmitted(true);
    } else {
      alert("Something went wrong. Please try again.");
    }
  };

  return (
    <div className="legal-page">
      <div className="legal-inner">
        <p className="legal-eyebrow">Get in touch</p>
        <h1 className="legal-title">We'd love to hear from you.</h1>
        <p className="legal-date">Questions, feedback, or just want to say hello — we're here.</p>
        <div className="legal-divider" />
        {submitted ? (
          <div className="contact-success">
            <div className="contact-success-icon">✓</div>
            <h2>Message sent!</h2>
            <p>Thanks for reaching out. We'll get back to you as soon as possible.</p>
          </div>
        ) : (
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="name">Name</label>
              <input id="name" name="name" type="text" placeholder="Your name" value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input id="email" name="email" type="email" placeholder="you@example.com" value={form.email} onChange={handleChange} required />
            </div>
            <div className="form-group">
              <label htmlFor="message">Message</label>
              <textarea id="message" name="message" placeholder="What's on your mind?" rows={6} value={form.message} onChange={handleChange} required />
            </div>
            <input type="text" name="_gotcha" style={{ display: "none" }} tabIndex="-1" autoComplete="off" />
            <button type="submit" className="btn-submit">Send message</button>
          </form>
        )}
      </div>
    </div>
  );
}

// ── FAQ page ──────────────────────────────────────────────────

function FAQPage() {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    { q: "What is Kairos?", a: "Kairos is a focus timer app built around the Pomodoro Technique. It helps you build deep work habits through timed focus sessions, streak tracking, and session analytics." },
    { q: "Is Kairos free to use?", a: "Yes — Kairos is free to download and use. Some advanced features may require a subscription in the future, but the core timer and streak features will always be free." },
    { q: "How does the streak system work?", a: "Complete at least one focus session per day to keep your streak alive. Miss a day and it resets to zero. Your longest streak is always saved so you can try to beat it." },
    { q: "What is the Pomodoro Technique?", a: "The Pomodoro Technique is a time management method where you work in focused intervals — typically 25 minutes — followed by a short break. It's one of the most research-backed ways to improve focus and reduce mental fatigue." },
    { q: "Can I customise my session length?", a: "Yes. You can adjust your focus session and break durations in the app settings to match your personal workflow." },
    { q: "Is my data private?", a: "Yes. We take privacy seriously and never sell your personal data. You can read our full Privacy Policy for details on what we collect and how we use it." },
    { q: "Which devices is Kairos available on?", a: "Kairos is currently available on iOS. Android support is on our roadmap." },
    { q: "I found a bug. How do I report it?", a: "We'd love to hear about it. Send us a message through the Contact page" },
  ];

  return (
    <div className="legal-page">
      <div className="legal-inner">
        <p className="legal-eyebrow">Help</p>
        <h1 className="legal-title">Frequently asked questions.</h1>
        <p className="legal-date">Can't find your answer? <a href="/contact">Contact us</a></p>
        <div className="legal-divider" />
        <div className="faq-list">
          {faqs.map((faq, i) => (
            <div
              key={i}
              className={`faq-item${openIndex === i ? " open" : ""}`}
              onClick={() => setOpenIndex(openIndex === i ? null : i)}
            >
              <div className="faq-question">
                <span>{faq.q}</span>
                <span className="faq-icon">{openIndex === i ? "−" : "+"}</span>
              </div>
              {openIndex === i && <p className="faq-answer">{faq.a}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── App ───────────────────────────────────────────────────────

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <Nav />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faq" element={<FAQPage />} />
      </Routes>
      <Analytics />
      <SpeedInsights />
      <Footer />
    </BrowserRouter>
  );
}